import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import Swal from 'sweetalert2';

type Department = 'Purchase' | 'Supplies';

const LoginPage: React.FC = () => {
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const { theme, toggleTheme } = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login, isAuthenticated, user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect authenticated users
  useEffect(() => {
    if (isAuthenticated && user && !authLoading) {
      const from = (location.state as any)?.from?.pathname || 
                  (user.department === 'Supplies' ? '/supplies' : '/purchase');
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, user, authLoading, navigate, location.state]);

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="login-container">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-4">
              <div className="card login-card">
                <div className="card-body p-4 text-center">
                  <div className="spinner-border text-orange" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-muted mt-2">Checking authentication...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleDepartmentSelect = (department: Department) => {
    setSelectedDepartment(department);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedDepartment) {
      Swal.fire({
        icon: 'warning',
        title: 'Department Required',
        text: 'Please select a department (Purchase or Supplies)',
        confirmButtonColor: '#ff6b35'
      });
      return;
    }

    if (!username || !password) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please enter both username and password',
        confirmButtonColor: '#ff6b35'
      });
      return;
    }

    setIsLoading(true);
    try {
      await login(username, password, selectedDepartment);
      
      Swal.fire({
        icon: 'success',
        title: 'Login Successful',
        text: `Welcome to ${selectedDepartment} department!`,
        timer: 1500,
        showConfirmButton: false
      });

      // Navigate based on department
      if (selectedDepartment === 'Supplies') {
        navigate('/supplies');
      } else {
        navigate('/purchase');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Login Failed',
        text: error.message || 'Invalid credentials. Please try again.',
        confirmButtonColor: '#ff6b35'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedDepartment(null);
    setUsername('');
    setPassword('');
  };

  return (
    <div className="login-container">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6 col-xl-5 position-relative">
            <div className="card login-card">
              <div className="card-body p-5 position-relative">
                {/* Theme switcher in top right */}
                <div className='theme-switcher-container'>
                  <button
                    className="theme-switcher btn"
                    onClick={toggleTheme}
                    title={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
                  >
                    <i className={`bi bi-${theme === 'light' ? 'moon' : 'sun'}-fill theme-switcher-icon`}></i>
                  </button>
                </div>
                {/* Header */}
                <div className="text-center mb-5">
                  <h2 className="text-orange fw-bold mb-3">Tay Supplies Management</h2>
                  <p className="text-muted fs-5">
                    {!selectedDepartment ? 'Choose your department' : `Login to ${selectedDepartment}`}
                  </p>
                </div>

                {/* Department Selection */}
                {!selectedDepartment && (
                  <div className="row g-4">
                    <div className="col-6">
                      <div 
                        className="card choice-card h-100 text-center p-4"
                        onClick={() => handleDepartmentSelect('Purchase')}
                      >
                        <i className="bi bi-cart3 text-orange department-icon"></i>
                        <h5 className="mt-3 mb-0 fw-semibold">Purchase</h5>
                      </div>
                    </div>
                    <div className="col-6">
                      <div 
                        className="card choice-card h-100 text-center p-4"
                        onClick={() => handleDepartmentSelect('Supplies')}
                      >
                        <i className="bi bi-boxes text-orange department-icon"></i>
                        <h5 className="mt-3 mb-0 fw-semibold">Supplies</h5>
                      </div>
                    </div>
                  </div>
                )}

                {/* Login Form */}
                {selectedDepartment && (
                  <form onSubmit={handleLogin}>
                    <div className="mb-4">
                      <label htmlFor="username" className="form-label fs-5 fw-semibold">Username</label>
                      <input
                        type="text"
                        className="form-control fs-6"
                        id="username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        disabled={isLoading}
                        autoComplete="username"
                        autoFocus
                        placeholder="Enter your username"
                      />
                    </div>

                    <div className="mb-5">
                      <label htmlFor="password" className="form-label fs-5 fw-semibold">Password</label>
                      <div className="input-group">
                        <input
                          type={showPassword ? "text" : "password"}
                          className="form-control fs-6"
                          id="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          disabled={isLoading}
                          autoComplete="current-password"
                          placeholder="Enter your password"
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={isLoading}
                          title={showPassword ? "Hide password" : "Show password"}
                        >
                          <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                        </button>
                      </div>
                    </div>

                    <div className="d-grid gap-3">
                      <button 
                        type="submit" 
                        className="btn btn-orange btn-lg fs-5 py-3"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Logging in...
                          </>
                        ) : (
                          'Login'
                        )}
                      </button>
                      
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-lg fs-6 py-2"
                        onClick={handleBack}
                        disabled={isLoading}
                      >
                        Back to Department Selection
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
